// pages/EndowmentCentre.jsx
import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Alert,
  Modal,
} from "react-bootstrap";
import Sidebar from "../components/dashboard/Sidebar";
import DashboardHeader from "../components/dashboard/DashboardHeader";
import EndowmentForm from "../components/endowment/EndowmentForm";
import CategoryBrowser from "../components/endowment/CategoryBrowser";

// Import modals
import { DeleteEndowmentModal } from "../components/modals/EndowmentModals/DeleteEndowmentModal";
import ConfirmModal from "../components/modals/EndowmentModals/ConfirmModal";
import TermsAndConditionsModal from "../components/modals/EndowmentModals/TermsAndConditionsModal";
import AddToEndowmentModal from "../components/modals/EndowmentModals/AddToEndowmentModal";
import ViewCardModal from "../components/modals/BankCardModals/ViewCardModal";
import ViewPasswordModal from "../components/modals/PasswordModals/ViewPasswordModal";
import FileViewerEndowment from "../components/modals/FileModals/FileViewerEndowment";
import ViewPhraseModal from "../components/modals/BlockchainModals/ViewPhraseModal";
import { usePaymentStatus } from "../hooks/usePaymentStatus";

import { viewBankCard } from "../services/bankcard.service";
import { viewPassword } from "../services/password.service";
import { viewPhrase } from "../services/blockchain.service";

// Import hooks
import { useEndowment } from "../hooks/useEndowment";
import { useNavigation } from "../hooks/useNavigation";
import { useModals } from "../hooks/useModals";
import { useAuth } from "../contexts/AuthContext";

// Import services
import { fetchFileContent, checkPaymentStatus } from "../services/api.service";

import "../styles/pages/endowments.css";
import { STREAM_ENDPOINTS, DOWNLOAD_ENDPOINTS } from "../utils/constants";

const EndowmentCentre = () => {
  // Auth context
  const { token } = useAuth();

  // Sidebar state
  const [sidebarToggled, setSidebarToggled] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [isDeletingFolder, setIsDeletingFolder] = useState(false);

  const [showResendConfirmModal, setShowResendConfirmModal] = useState(false);
  const [originalRecipientEmail, setOriginalRecipientEmail] = useState("");
  // Custom hooks
  const {
    endowment,
    endowmentExists,
    isLoading,
    alias,
    recipient,
    releaseDate,
    maxReleaseDate,
    termsAccepted,
    isEditing,
    recipientStatus,
    originalValues,
    selectedItems,
    isSaving,
    isDeleting,
    isAddingToEndowment,
    isResending,
    alert,
    setAlias,
    setReleaseDate,
    setTermsAccepted,
    setIsEditing,
    setSelectedItems,
    setAlert,
    loadEndowment,
    handleCreateEndowment,
    handleEditEndowment,
    handleDeleteEndowment,
    handleResendEndowment,
    handleRecipientChange,
    handleAddItems,
    handleRemoveItem,
    showAlert,
  } = useEndowment();

  const {
    currentPath,
    searchTerm,
    sortMethod,
    sortOrder,
    handleBackClick,
    handleCategoryClick,
    handleSearch,
    handleSort,
  } = useNavigation();

  const {
    deleteModalOpen,
    confirmCreateModalOpen,
    confirmEditModalOpen,
    termsModalOpen,
    addModalOpen,
    confirmDeleteItemModalOpen,
    resendModalOpen,
    progressModalOpen,
    viewFileModalOpen,
    viewPasswordModalOpen,
    viewCardModalOpen,
    viewBlockchainModalOpen,
    currentItem,
    itemToRemove,
    currentAddPath,
    addModalSearchTerm,
    addModalFilterOption,
    setCurrentAddPath,
    setAddModalSearchTerm,
    setAddModalFilterOption,
    openDeleteModal,
    openConfirmCreateModal,
    openConfirmEditModal,
    openTermsModal,
    openAddModal,
    openConfirmDeleteItemModal,
    openResendModal,
    closeDeleteModal,
    closeConfirmCreateModal,
    closeConfirmEditModal,
    closeTermsModal,
    closeAddModal,
    closeConfirmDeleteItemModal,
    closeResendModal,
    closeProgressModal,
    confirmDeleteFolderModalOpen,
    folderToRemove,
    openConfirmDeleteFolderModal,
    closeConfirmDeleteFolderModal,
    openViewFileModal,
    openViewPasswordModal,
    openViewCardModal,
    openViewBlockchainModal,
    closeViewFileModal,
    closeViewPasswordModal,
    closeViewCardModal,
    closeViewBlockchainModal,
    handleAddModalCategoryClick,
    handleAddModalBackClick,
  } = useModals();

  const [tempSelectedItems, setTempSelectedItems] = useState({
    files: [],
    passwords: [],
    banking: [],
    blockchain: [],
  });

  const { isAccountFrozen, showFrozenAlert } = usePaymentStatus();

  // Handle form edit/save
  // Handle form edit/save
  const handleEdit = async () => {
  if (isAccountFrozen) {
    showFrozenAlert(showAlert);
    return;
  }
  if (isEditing) {
    // Save changes
    if (!termsAccepted) {
      showAlert("Please accept the Terms and Conditions", "danger");
      return;
    }

    // Validate inputs
    if (!alias || !recipient || !releaseDate) {
      showAlert("All fields are required", "danger");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipient)) {
      showAlert("Please enter a valid email address", "danger");
      return;
    }

    // Check if endowment was rejected and email hasn't changed (PRIORITY 1)
    if (
      recipientStatus?.is_declined &&
      recipient === originalRecipientEmail &&
      endowmentExists
    ) {
      setShowResendConfirmModal(true);
      return; // Don't proceed with save
    }

    // Check if nothing changed at all (PRIORITY 2)
    if (
      endowmentExists &&
      alias === originalValues.alias &&
      recipient === originalValues.recipient
    ) {
      showAlert(
        "No changes detected. Please modify the alias or recipient email to save changes.",
        "warning"
      );
      return;
    }

    // Check payment status before proceeding
    try {
      const paymentStatus = await checkPaymentStatus();
      if (paymentStatus.isOverdue) {
        showAlert(
          "Your account is currently frozen. Please visit My Profile > Account to make payment and restore full access.",
          "danger"
        );
        return;
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
    }

    // Open appropriate confirmation modal
    if (endowment) {
      openConfirmEditModal();
    } else {
      openConfirmCreateModal();
    }
  } else {
    // Enable editing mode - store original email
    setOriginalRecipientEmail(recipient);
    setIsEditing(true);
  }
};

  // Handle form cancel
  const handleCancel = () => {
    // Reset to original values
    setAlias(originalValues.alias);
    handleRecipientChange(originalValues.recipient);
    setReleaseDate(originalValues.releaseDate);
    setIsEditing(false);
    setTermsAccepted(false);
  };

  // Handle delete button click
  const handleDelete = () => {
    openDeleteModal();
  };

  // Handle resend button click
  const handleResend = async () => {
    try {
      await handleResendEndowment();
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  // Handle view terms and conditions
  const handleViewTerms = () => {
    openTermsModal();
  };

  // Handle add items to endowment
  const handleAddItemsClick = async () => {
    if (isAccountFrozen) {
      showFrozenAlert(showAlert);
      return;
    }
    if (!endowment) {
      showAlert("Please create an endowment first", "warning");
      return;
    }

    try {
      // Check payment status before proceeding
      const paymentStatus = await checkPaymentStatus();
      if (paymentStatus.isOverdue) {
        showAlert(
          "Your account is currently frozen. Please visit My Profile > Account to make payment and restore full access.",
          "warning"
        );
        return;
      }

      // Reset add modal state AND clear temporary selections
      setCurrentAddPath([]);
      setAddModalSearchTerm("");
      setAddModalFilterOption("all");

      // IMPORTANT: Reset temporary selections when opening modal
      setTempSelectedItems({
        files: [],
        passwords: [],
        banking: [],
        blockchain: [],
      });

      // Open add modal
      openAddModal();

      setTimeout(() => {
      }, 200);
    } catch (error) {
      showAlert(`Error: ${error.message}`, "danger");
    }
  };

  // Handle item view
  const handleViewItem = async (item, category) => {
    try {
      // Determine the bucket based on category
      let bucket;
      switch (category.toLowerCase()) {
        case "files":
          bucket = "files";
          break;
        case "passwords":
          bucket = "passwords";
          break;
        case "banking":
          bucket = "bankcards";
          break;
        case "blockchain":
          bucket = "blockchain";
          break;
        default:
          throw new Error(`Invalid category: ${category}`);
      }

      // For files, we need to fetch the actual file content and create a blob URL
      if (category.toLowerCase() === "files") {
        try {
          const fileName = item.name || item;
          const extension = fileName.split(".").pop()?.toLowerCase();

          // Define file types that can be viewed directly via presigned URL
          const mediaExtensions = [
            "jpg",
            "jpeg",
            "png",
            "gif",
            "bmp",
            "webp",
            "svg",
            "mp4",
            "avi",
            "mov",
            "mkv",
            "webm",
            "mp3",
            "wav",
            "pdf",
          ];

          // Define file types that need server streaming for text content
          const textExtensions = [
            "txt",
            "js",
            "jsx",
            "ts",
            "tsx",
            "json",
            "xml",
            "html",
            "css",
            "md",
            "py",
            "java",
            "c",
            "cpp",
            "php",
            "rb",
            "go",
            "rs",
            "sh",
            "yml",
            "yaml",
            "ini",
            "cfg",
            "log",
          ];

          const isMediaFile = mediaExtensions.includes(extension);
          const isTextFile = textExtensions.includes(extension);

          if (isMediaFile) {
            // Use presigned URL for media files (images, videos, PDFs)

            const presignedResponse = await fetch(
              STREAM_ENDPOINTS.GENERATE_VIEW_URL,
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  bucket: bucket,
                  fileName: fileName,
                  expiresIn: 3600, // 1 hour for viewing
                }),
                credentials: "include",
              }
            );

            if (!presignedResponse.ok) {
              throw new Error(
                `Failed to generate view URL: ${presignedResponse.status}`
              );
            }

            const { viewUrl } = await presignedResponse.json();

            // For media files, we can use the presigned URL directly
            const itemWithData = {
              fileName: fileName,
              name: fileName,
              originalName: fileName,
              type: category.toLowerCase(),
              bucket: bucket,
              contentType: getContentTypeFromFileName(fileName),
              url: viewUrl, // Use presigned URL directly
              isPresignedUrl: true, // Flag to indicate this is a presigned URL
            };

            openViewFileModal(itemWithData);
          } else {
            // Use server streaming for text files and other files that need processing
            

            const response = await fetch(STREAM_ENDPOINTS.FILE, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                bucket: bucket,
                fileName: fileName,
              }),
              credentials: "include",
            });

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            const contentType =
              response.headers.get("Content-Type") ||
              getContentTypeFromFileName(fileName);

            // Create object URL for the blob
            const url = URL.createObjectURL(blob);

            // For text files, also get the text content
            let textContent = null;
            if (contentType.startsWith("text/") || isTextFile) {
              textContent = await blob.text();
            }

            // Create the properly formatted file data object
            const itemWithData = {
              fileName: fileName,
              name: fileName,
              originalName: fileName,
              type: category.toLowerCase(),
              bucket: bucket,
              contentType: contentType,
              url: url,
              blob: blob,
              textContent: textContent,
              isPresignedUrl: false, // Flag to indicate this is a blob URL
            };

            openViewFileModal(itemWithData);
          }
        } catch (error) {
          console.error("Error fetching file:", error);
          showAlert(`Error loading file: ${error.message}`, "danger");
        }
      } else {
        // For other types, decrypt the data using the appropriate service
        let decryptedData;
        const itemName = item.name || item;

        try {
          switch (category.toLowerCase()) {
            case "passwords":
              decryptedData = await viewPassword(itemName, token);
              break;
            case "banking":
              decryptedData = await viewBankCard(itemName, token);
              break;
            case "blockchain":
              decryptedData = await viewPhrase(itemName, token);
              break;
            default:
              throw new Error(`Invalid category: ${category}`);
          }

          // Create properly formatted item with decrypted data
          const itemWithData = {
            name: itemName,
            data: decryptedData,
            type: category.toLowerCase(),
            originalName: itemName,
            // Spread the decrypted data for direct access
            ...decryptedData,
          };

          // Open the appropriate modal
          switch (category.toLowerCase()) {
            case "passwords":
              openViewPasswordModal(itemWithData);
              break;
            case "banking":
              openViewCardModal(itemWithData);
              break;
            case "blockchain":
              openViewBlockchainModal(itemWithData);
              break;
          }
        } catch (error) {
          console.error(`Error decrypting ${category} data:`, error);
          showAlert(`Error loading ${category}: ${error.message}`, "danger");
        }
      }
    } catch (error) {
      showAlert(`Error loading item: ${error.message}`, "danger");
    }
  };

  // Helper function to get content type from file name
  const getContentTypeFromFileName = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();

    const mimeTypes = {
      // Images
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      bmp: "image/bmp",
      svg: "image/svg+xml",

      // Documents
      pdf: "application/pdf",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ppt: "application/vnd.ms-powerpoint",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",

      // Text
      txt: "text/plain",
      html: "text/html",
      css: "text/css",
      js: "text/javascript",
      json: "application/json",
      xml: "text/xml",

      // Audio
      mp3: "audio/mpeg",
      wav: "audio/wav",
      ogg: "audio/ogg",
      flac: "audio/flac",

      // Video
      mp4: "video/mp4",
      avi: "video/avi",
      mov: "video/quicktime",
      wmv: "video/x-ms-wmv",
      mkv: "video/x-matroska",

      // Archives
      zip: "application/zip",
      rar: "application/x-rar-compressed",
      "7z": "application/x-7z-compressed",
    };

    return mimeTypes[extension] || "application/octet-stream";
  };

  // Handle remove item
  const handleRemoveItemClick = (itemName, itemType) => {
    openConfirmDeleteItemModal({ name: itemName, type: itemType });
  };

  const handleRemoveFolderClick = (folderItem, category) => {

    // Get all files that would be removed
    const folderPath = folderItem.fullPath || folderItem.name;
    const allFiles = endowment?.selectedItems?.files || [];
    const folderPrefix = folderPath.endsWith("/")
      ? folderPath
      : `${folderPath}/`;
    const filesToRemove = allFiles.filter((filePath) =>
      filePath.startsWith(folderPrefix)
    );

    // Add file count to folder item for the modal
    const folderWithCount = {
      ...folderItem,
      fileCount: filesToRemove.length,
      filesToRemove: filesToRemove,
      category: category,
    };

    openConfirmDeleteFolderModal(folderWithCount);
  };

  // Confirm remove item
  const confirmRemoveItem = async () => {
    if (!itemToRemove) return;

    try {
      await handleRemoveItem(itemToRemove.name, itemToRemove.type);
      closeConfirmDeleteItemModal();
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  const confirmRemoveFolder = async () => {
    if (!folderToRemove) return;

    try {
      setIsDeletingFolder(true);

      const filesToRemove = folderToRemove.filesToRemove || [];

      if (filesToRemove.length === 0) {
        showAlert("No files found in this folder to remove", "warning");
        closeConfirmDeleteFolderModal();
        return;
      }

      // Remove all files from the folder at once
      const allFiles = endowment?.selectedItems?.files || [];
      const updatedItems = {
        files: allFiles.filter((filePath) => !filesToRemove.includes(filePath)),
        passwords: [...(endowment.selectedItems.passwords || [])],
        banking: [...(endowment.selectedItems.banking || [])],
        blockchain: [...(endowment.selectedItems.blockchain || [])],
      };

      // Update the endowment
      const { uploadInheritance } = await import(
        "../services/endowment.service"
      );
      const userDetails = JSON.parse(
        sessionStorage.getItem("userDetails") || "{}"
      );
      const userEmail = userDetails.Email || "";

      await uploadInheritance(
        {
          alias: endowment.alias,
          account: endowment.account,
          selectedItems: updatedItems,
          duration: endowment.duration,
          Email: userEmail,
        },
        token
      );

      // Close modal first, then reload and show success
      closeConfirmDeleteFolderModal();

      // Reload the endowment to refresh the display
      await loadEndowment(true);

      showAlert(
        `Successfully removed folder "${folderToRemove.name}" with ${filesToRemove.length} files`,
        "success"
      );
    } catch (error) {
      console.error("Error removing folder:", error);
      showAlert(`Error removing folder: ${error.message}`, "danger");
      closeConfirmDeleteFolderModal();
    } finally {
      setIsDeletingFolder(false);
    }
  };

  // Handle create endowment confirmation
  const handleCreateConfirm = async () => {
    try {
      await handleCreateEndowment();
      closeConfirmCreateModal();
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  // Handle edit endowment confirmation
  const handleEditConfirm = async () => {
    try {
      await handleEditEndowment();
      closeConfirmEditModal();
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  // Handle delete endowment confirmation
  const handleDeleteConfirm = async () => {
    try {
      await handleDeleteEndowment();
      closeDeleteModal();
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  // Handle resend endowment confirmation
  const handleResendConfirm = async () => {
    try {
      await handleResendEndowment();
      setIsEditing(false); // Exit edit mode
      setShowResendConfirmModal(false); // Close modal only after success
    } catch (error) {
      setShowResendConfirmModal(false); // Close modal even on error
      // Error is already handled in handleResendEndowment
    }
  };

  const handleResendCancel = () => {
    setShowResendConfirmModal(false);
    // Stay in edit mode so user can change the email
  };

  // Handle save added items
  const handleSaveAddedItems = async (selectedAddItems) => {
    try {
      await handleAddItems(selectedAddItems);
      closeAddModal();
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  // Handle navigation for category browser
  const handleCategoryNavigation = (pathOrCategory) => {
    if (Array.isArray(pathOrCategory)) {
      // If it's an array, set the entire path
      handleCategoryClick(pathOrCategory);
    } else if (typeof pathOrCategory === "string") {
      // If it's a string, navigate to that category
      handleCategoryClick([pathOrCategory]);
    } else {
      // If it's empty, go to root
      handleCategoryClick([]);
    }
  };

  return (
    <div id="page-top">
      <div id="wrapper" className="d-flex">
        <Sidebar toggled={sidebarToggled} setToggled={setSidebarToggled} />

        <div
          id="content-wrapper"
          className={`d-flex flex-column ${
            sidebarToggled ? "content-toggled" : ""
          }`}
        >
          <div id="content">
            <DashboardHeader
              toggleSidebar={() => setSidebarToggled(!sidebarToggled)}
            />

            <div className="container-fluid">
              {/* Alert */}
              {alert.show && (
                <Alert
                  variant={alert.variant}
                  onClose={() => setAlert((prev) => ({ ...prev, show: false }))}
                  dismissible
                  className="mt-3"
                >
                  {alert.message}
                </Alert>
              )}

              {/* Endowment Header Form */}
              <EndowmentForm
                alias={alias}
                setAlias={setAlias}
                recipient={recipient}
                handleRecipientChange={handleRecipientChange}
                releaseDate={releaseDate}
                setReleaseDate={setReleaseDate}
                maxReleaseDate={maxReleaseDate}
                termsAccepted={termsAccepted}
                setTermsAccepted={setTermsAccepted}
                isEditing={isEditing}
                endowmentExists={endowmentExists}
                recipientStatus={recipientStatus}
                isLoading={isLoading}
                onEdit={handleEdit}
                onCancel={handleCancel}
                onViewTerms={handleViewTerms}
                onRefresh={() => loadEndowment(true)}
                onDelete={handleDelete}
                onResend={handleResend}
                isResending={isResending}
              />

              {/* Main Endowment Content */}
              <div className="row">
                <div className="col-lg-12">
                  <div
                    className="card category-card category-card-green shadow mb-4"
                    style={{
                      height: "calc(100vh - 360px)",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      className="card-body"
                      style={{ flexGrow: 1, overflowY: "auto" }}
                    >
                      {isLoading ? (
                        <div className="text-center py-5">
                          <Spinner
                            animation="border"
                            role="status"
                            variant="primary"
                          >
                            <span className="sr-only">Loading...</span>
                          </Spinner>
                        </div>
                      ) : !endowment ? (
                        <div className="endow-empty-state w-100 px-4 py-5 text-center bg-light rounded shadow-sm">
                          <i className="fas fa-child fa-3x text-dark mb-4"></i>
                          <h4 className="text-dark fw-bold">
                            No Endowment Created Yet
                          </h4>
                          <p className="text-black mb-3">
                            You need to create an endowment using the form
                            above.
                          </p>
                          <p className="text-black">
                            An endowment allows you to pass your digital assets
                            to a trusted recipient.
                          </p>

                          <div
                            className="mt-5 text-start mx-auto"
                            style={{ maxWidth: "1200px" }}
                          >
                            <h5 className="text-dark mb-4 fw-semibold">
                              How Endowments Work:
                            </h5>

                            <ul className="list-unstyled text-black">
                              <li className="mb-3 d-flex align-items-start">
                                <i className="fas fa-envelope me-3 text-primary mt-1"></i>
                                <span>
                                  Your recipient will receive an email with
                                  secure login credentials to access their
                                  endowment.
                                </span>
                              </li>
                              <li className="mb-3 d-flex align-items-start">
                                <i className="fas fa-check-circle me-3 text-success mt-1"></i>
                                <span>
                                  They must follow the provided link and
                                  acknowledge the endowment before it can be
                                  released to them.
                                </span>
                              </li>
                              <li className="mb-3 d-flex align-items-start">
                                <i className="fas fa-calendar me-3 text-warning mt-1"></i>
                                <span>
                                  On the release date, your recipient will be
                                  notified via email that their endowment is now
                                  available.
                                </span>
                              </li>
                              <li className="mb-3 d-flex align-items-start">
                                <i className="fas fa-lock me-3 text-secondary mt-1"></i>
                                <span>
                                  You can include files, passwords, banking
                                  information, and blockchain assets in your
                                  endowment.
                                </span>
                              </li>
                              <li className="d-flex align-items-start">
                                <i className="fas fa-unlock me-3 text-dark mt-1"></i>
                                <span>
                                  Once released, your recipient will have secure
                                  access to all items within their endowment.
                                </span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      ) : (
                        <CategoryBrowser
                          currentPath={currentPath}
                          onBackClick={handleBackClick}
                          onCategoryClick={handleCategoryNavigation}
                          searchTerm={searchTerm}
                          onSearch={handleSearch}
                          sortMethod={sortMethod}
                          sortOrder={sortOrder}
                          onSort={handleSort}
                          endowment={endowment}
                          isLoading={isLoading}
                          onViewItem={handleViewItem}
                          onRemoveItem={handleRemoveItemClick}
                          onRemoveFolder={handleRemoveFolderClick}
                        />
                      )}
                    </div>
                  </div>

                  {/* Floating Button - only show if endowment exists */}
                  {endowment && (
                    <div id="floatingButtonContainer">
                      <Button
                        id="addEndowmentButton"
                        className="btn-icon-add"
                        onClick={handleAddItemsClick}
                        disabled={isLoading}
                      >
                        <i className="fas fa-plus"></i>
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}

      {/* Delete Endowment Modal */}
      <DeleteEndowmentModal
        show={deleteModalOpen}
        onHide={closeDeleteModal}
        endowmentName={alias}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      />

      {/* Create Endowment Confirmation Modal */}
      <ConfirmModal
        show={confirmCreateModalOpen}
        onHide={closeConfirmCreateModal}
        title="Create Endowment"
        body={`Are you sure you want to create an endowment for ${recipient}?`}
        confirmText="Create"
        confirmVariant="success"
        onConfirm={handleCreateConfirm}
        isLoading={isSaving}
      />

      {/* Edit Endowment Confirmation Modal */}
      <ConfirmModal
        show={confirmEditModalOpen}
        onHide={closeConfirmEditModal}
        title="Edit Endowment"
        body={`Are you sure you want to save changes to this endowment?`}
        confirmText="Save Changes"
        confirmVariant="success"
        onConfirm={handleEditConfirm}
        isLoading={isSaving}
      />

      {/* Resend Endowment Confirmation Modal */}
      <ConfirmModal
        show={resendModalOpen}
        onHide={closeResendModal}
        title="Resend Endowment"
        body={`Are you sure you want to resend this endowment to ${recipient}?`}
        confirmText="Resend"
        confirmVariant="warning"
        onConfirm={handleResendConfirm}
        isLoading={isResending}
      />

      {/* Delete Item Confirmation Modal */}
      <ConfirmModal
        show={confirmDeleteItemModalOpen}
        onHide={closeConfirmDeleteItemModal}
        title="Remove Item"
        body={
          itemToRemove
            ? `Are you sure you want to remove ${itemToRemove.name} from the endowment?`
            : ""
        }
        confirmText="Remove"
        confirmVariant="danger"
        onConfirm={confirmRemoveItem}
        isLoading={isDeleting}
      />

      <ConfirmModal
        show={confirmDeleteFolderModalOpen}
        onHide={closeConfirmDeleteFolderModal}
        title="Remove Folder"
        body={
          folderToRemove ? (
            <div>
              <p className="text-white">
                Are you sure you want to remove the folder{" "}
                <strong>"{folderToRemove.name}"</strong> from your endowment?
              </p>
              <p className="text-white">
                This will remove{" "}
                <strong>
                  {folderToRemove.fileCount} file
                  {folderToRemove.fileCount !== 1 ? "s" : ""}
                </strong>{" "}
                from your endowment:
              </p>
              <div
                style={{
                  maxHeight: "200px",
                  overflowY: "auto",
                  backgroundColor: "#f8f9fa",
                  padding: "10px",
                  borderRadius: "5px",
                  fontSize: "0.9em",
                }}
              >
                {folderToRemove.filesToRemove?.map((filePath, index) => (
                  <div key={index} style={{ marginBottom: "2px" }}>
                    <i className="fas fa-file fa-sm text-muted mr-2"></i>
                    {filePath}
                  </div>
                ))}
              </div>
              <p className="mt-3 text-danger mb-0">
                <strong>This action cannot be undone.</strong>
              </p>
            </div>
          ) : (
            ""
          )
        }
        confirmText="Remove Folder"
        confirmVariant="danger"
        onConfirm={confirmRemoveFolder}
        isLoading={isDeletingFolder}
      />

      {/* Terms and Conditions Modal */}
      <TermsAndConditionsModal
        show={termsModalOpen}
        onHide={closeTermsModal}
        onAccept={() => {
          setTermsAccepted(true);
          closeTermsModal();
        }}
      />

      {/* Progress Modal */}
      <Modal
        show={progressModalOpen}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Body className="text-center py-5">
          <Spinner animation="border" role="status" variant="primary" />
          <p className="mt-3 mb-0">Processing your request. Please wait...</p>
        </Modal.Body>
      </Modal>

      {/* Add to Endowment Modal */}
      <AddToEndowmentModal
        show={addModalOpen}
        onHide={() => {
          // Reset temp selections when closing modal
          setTempSelectedItems({
            files: [],
            passwords: [],
            banking: [],
            blockchain: [],
          });
          closeAddModal();
        }}
        currentPath={currentAddPath}
        onNavigate={setCurrentAddPath}
        onBack={handleAddModalBackClick}
        searchTerm={addModalSearchTerm}
        onSearchChange={(e) => setAddModalSearchTerm(e.target.value)}
        filterOption={addModalFilterOption}
        onFilterChange={(e) => setAddModalFilterOption(e.target.value)}
        onConfirm={handleSaveAddedItems}
        isLoading={isAddingToEndowment || isLoading}
        endowment={endowment}
        selectedItems={tempSelectedItems}
        setSelectedItems={setTempSelectedItems}
        onViewItem={handleViewItem}
      />

      {/* View Modals */}
      <FileViewerEndowment
        show={viewFileModalOpen}
        onHide={closeViewFileModal}
        fileData={currentItem}
        onRemove={(fileName) => {
          // Get the current category from your navigation state
          const category =
            currentPath[currentPath.length - 1]?.toLowerCase() || "files";
          handleRemoveItemClick(fileName, category);
        }}
      />

      <Modal
        show={showResendConfirmModal}
        onHide={handleResendCancel}
        centered
        backdrop="static"
      >
        <Modal.Header className=" text-dark">
          <Modal.Title>
            <i className="fas fa-exclamation-triangle me-2"></i>
            Recipient Email Unchanged
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <p className="mb-3 text-white">
            The recipient email has not been changed. This endowment was
            previously
            <span className="text-danger fw-bold"> rejected</span> by this
            recipient.
          </p>
          <p className="mb-0 text-white">
            Would you like to <strong>resend the invitation</strong> instead of
            saving the changes?
          </p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="outline-secondary" onClick={handleResendCancel}>
            <i className="fas fa-edit me-1"></i>
            No, let me change the email
          </Button>
          <Button
            variant="warning"
            onClick={handleResendConfirm}
            disabled={isResending}
          >
            <i
              className={`fas ${
                isResending ? "fa-spinner fa-spin" : "fa-paper-plane"
              } me-1`}
            ></i>
            {isResending ? "Resending..." : "Yes, resend invitation"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ViewPasswordModal
        show={viewPasswordModalOpen}
        onHide={closeViewPasswordModal}
        passwordData={currentItem}
      />

      <ViewCardModal
        show={viewCardModalOpen}
        onHide={closeViewCardModal}
        cardData={currentItem}
      />

      <ViewPhraseModal
        show={viewBlockchainModalOpen}
        onHide={closeViewBlockchainModal}
        phraseData={currentItem}
      />
    </div>
  );
};

export default EndowmentCentre;
